import React, { ReactNode, useContext, useEffect, useState } from 'react';
import Navigation from '../components/Navigation';
import { GlobalContext, StoreOffer } from '../context/GlobalContext';
import { ContainerSpinner } from './Spinner';
import Spinner from './Spinner';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Onboarding from './Onboarding';
import classNames from 'classnames';
import { AlertCircle, Copy, Check, RefreshCw, ShieldCheck, Tag, Smartphone, Laptop, Tablet, ChevronLeft, ChevronRight, Clock, MapPin, ChevronDown, Package, Truck, Zap } from 'lucide-react';
import { GetRoutePermissions, calculate_permissions_blacklist, calculate_permissions_whitelist } from '../hooks/firebase';
import { StoreAnnouncements } from './StoreAnnouncements';
import { StorePopup } from './StorePopup';
import { Dialog } from './Dialog';
import firestore from '../firestore';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useGoogleMapsEnabled } from '../context/GoogleMapsProvider';
import useFetch, { callAPI } from '../hooks/api';
import { UserData } from '../hooks/auth';
import { useIntercom } from '../hooks/intercom';
import EmbeddedSaleLayout from './EmbeddedSaleLayout';
import {
	embedded_sale_url,
	get_embedded_sale_context,
	LOGIN_PATH,
	normalize_route_path,
	SALE_PATH,
	without_pos_parameters
} from '../utils/embeddedSale';

export default function Layout({ children }: { children: ReactNode }) {
	const location = useLocation();
	const navigate = useNavigate();
	const embedded_context = get_embedded_sale_context(location);
	const route_path = normalize_route_path(location.pathname);
	const is_login = embedded_context ? route_path === LOGIN_PATH : location.pathname === LOGIN_PATH;
	const {
		authenticated,
		userData,
		shouldLogin,
		internal,
		accessToOtherStores,
		newVersionAvailable,
		isAdminAccount,
		internalPath,
		internalCountry,
		CountrySettings,
		storeAgentAuth,
		storeOffers,
	} = useContext(GlobalContext);
	const [isDesktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false);
	const currentInternalPath = internalPath.get();
	const { intercomOpen, intercomAvailable, openIntercom } = useIntercom();

	useEffect(() => {
		if (!internal) {
			setDesktopSidebarCollapsed(false);
		}
	}, [internal]);

	useEffect(() => {
		if (embedded_context) return;
		const params = new URLSearchParams(location.search);
		if (params.has('embed') || params.has('pos_type')) {
			navigate(
				{ pathname: location.pathname, search: without_pos_parameters(location.search), hash: location.hash },
				{ replace: true, state: location.state }
			);
		}
	}, [embedded_context, location, navigate]);

	const routePermissions = GetRoutePermissions();

	if (location.pathname === '/liveness-detection') {
		return <>{children}</>;
	}

	const permissionsBlacklist = calculate_permissions_blacklist(userData);
	const permissionsWhitelist = calculate_permissions_whitelist(userData, permissionsBlacklist || []);

	if (!authenticated && !shouldLogin) {
		if (embedded_context) {
			return (
				<EmbeddedSaleLayout userData={null}>
					<ContainerSpinner size={3} />
				</EmbeddedSaleLayout>
			);
		}
		return (
			<div className="h-svh w-full text-neutral-100 light:bg-background dark:bg-background">
				<ContainerSpinner size={3} />
			</div>
		);
	}
	if (shouldLogin && !is_login) {
		return <Navigate to={embedded_context ? embedded_sale_url(LOGIN_PATH, embedded_context) : LOGIN_PATH} replace />;
	}
	if (!shouldLogin && is_login) {
		return <Navigate to={embedded_context && !internal ? embedded_sale_url(SALE_PATH, embedded_context) : '/'} replace />;
	}
	if (!accessToOtherStores && location.pathname === '/log-in-as') {
		return <Navigate to="/" replace />;
	}
	if (!internal && location.pathname === '/internal') {
		return <Navigate to="/" replace />;
	}
	if (internal && location.pathname !== '/internal') {
		return <Navigate to="/internal" replace />;
	}
	if (userData && !internal && location.pathname === '/comisiones-vendedores' && userData.seller_commissions_access !== true) {
		return <Navigate to="/" replace />;
	}
	if (
		(permissionsBlacklist?.includes('new_sale') && route_path === SALE_PATH) ||
		(permissionsBlacklist?.includes('delete_sale') && location.pathname === '/deleteSale') ||
		(permissionsBlacklist?.includes('delete_payment') && location.pathname === '/tiendas/eliminar-pago') ||
		(permissionsBlacklist?.includes('change_sale_price') && location.pathname === '/tiendas/cambiar-precio') ||
		(permissionsBlacklist?.includes('register_payment') && location.pathname === '/buscarcliente') ||
		(permissionsBlacklist?.includes('recargas') && (location.pathname === '/tiendas/recargar' || location.pathname === '/reportes/recargas'))
	) {
		return <Navigate to="/" replace />;
	}
	if (
		!internal &&
		userData &&
		!userData.master_user &&
		(
			location.pathname === '/salud' ||
			location.pathname === '/integraciones' ||
			(location.pathname === '/recuperacion-cartera' && !permissionsWhitelist.includes('cobranza_portal')) ||
			(location.pathname === '/mis-promociones' && !permissionsWhitelist.includes('promotions_portal'))
		)
	) {
		return <Navigate to="/" replace />;
	}
	if (
		(userData?.is_active === false || userData?.master_store?.is_active === false) &&
		(route_path === SALE_PATH || ['/warranty', '/deleteSale', '/tiendas/eliminar-pago', '/tiendas/cambiar-precio', '/changeEmail'].includes(location.pathname))
	) {
		return <Navigate to="/" replace />;
	}

	if (embedded_context) {
		const canonical_url = embedded_sale_url(route_path === LOGIN_PATH ? LOGIN_PATH : SALE_PATH, embedded_context);
		if (`${location.pathname}${location.search}` !== canonical_url || location.hash) {
			return <Navigate to={canonical_url} replace state={location.state} />;
		}
	}

	const showContent =
		['/login', '/logout', '/internal'].includes(location.pathname) || (userData && authenticated && permissionsBlacklist);
	const showOnboarding =
		!internal &&
		userData?.registration_steps &&
		!Object.values(userData.registration_steps).every((step) => step === true);
	const showNavigation = showContent && !showOnboarding && !['/login'].includes(location.pathname);

	if (embedded_context) {
		return (
			<EmbeddedSaleLayout userData={userData}>
				{showContent && showOnboarding && <Onboarding />}
				{showContent && !showOnboarding && children}
				{!showContent && <ContainerSpinner size={3} />}
				{newVersionAvailable && <NewVersionBanner />}
				{storeAgentAuth && userData && <AgentAuthNotice authCode={storeAgentAuth} email={userData.email} intercomOpen={false} />}
			</EmbeddedSaleLayout>
		);
	}
	const webBanners = CountrySettings?.web_banners ?? (CountrySettings?.web_banner ? [CountrySettings.web_banner] : []);
	const activeWebBanners = webBanners.filter(
		(banner) => banner.is_active !== false && (banner.title?.trim() || banner.description?.trim())
	);
	const showBanner =
		!newVersionAvailable &&
		showNavigation &&
		activeWebBanners.length > 0 &&
		((location.pathname === '/' && !internal) || (internal && currentInternalPath === '/registrar-pago'));

	return (
		<div className="flex h-svh flex-col-reverse text-neutral-100 light:bg-background dark:bg-background md:flex-row light:md:bg-foreground">
			{showNavigation && (
				<Navigation
					CountrySettings={CountrySettings}
					userData={userData!}
					accessToOtherStores={accessToOtherStores}
					internal={internal}
					internalPath={internalPath}
					internalCountry={internalCountry}
					permissionsBlacklist={permissionsBlacklist || []}
					permissionsWhitelist={permissionsWhitelist}
					routePermissions={routePermissions}
					isDesktopCollapsed={internal && isDesktopSidebarCollapsed}
					setDesktopCollapsed={setDesktopSidebarCollapsed}
					isAdminAccount={isAdminAccount}
					sellerCommissionsAvailable={userData?.seller_commissions_access === true}
					intercomAvailable={intercomAvailable}
					intercomOpen={intercomOpen}
					openIntercom={openIntercom}
				/>
			)}

			<div
				className={classNames(
					'mb-[4.5rem] h-full w-full overflow-y-auto md:ml-[4rem] md:flex-[1] md:overflow-y-hidden md:p-[1.25rem]',
					internal && isDesktopSidebarCollapsed ? 'xl:ml-[3rem]' : 'xl:ml-[18rem]',
					{
						'!mb-0 !ml-0': !showNavigation
					}
				)}
			>
				<div className="h-full w-full light:bg-background md:overflow-y-auto md:rounded-xl md:bg-foreground">
					{showContent && showOnboarding && <Onboarding />}
					{showContent && !showOnboarding && (
						<>
							{showBanner && <StoreAnnouncements announcements={activeWebBanners} />}
							{children}
						</>
					)}
					{!showContent && <ContainerSpinner size={3} />}
				</div>
			</div>

			{!internal && !newVersionAvailable && showNavigation && userData?.country && (
				<StorePopupLoader
					key={userData.country}
					country={userData.country}
					isMasterStore={userData.master_user === true}
					storeEmail={userData.email}
				/>
			)}
			{newVersionAvailable && <NewVersionBanner />}
			{storeAgentAuth && userData && <AgentAuthNotice authCode={storeAgentAuth} email={userData.email} intercomOpen={intercomOpen} />}
			{storeOffers.length > 0 && userData && <StoreOffersNotice offers={storeOffers} offsetForAgentAuth={!!storeAgentAuth} userData={userData} />}

		</div>
	);
}

function StorePopupLoader({
	country,
	isMasterStore,
	storeEmail
}: {
	country: string;
	isMasterStore: boolean;
	storeEmail: string;
}) {
	const { data: storePopup } = useFetch(
		'https://api.krece.app/administration/store-popups/active/',
		'GET',
		true,
		{ country }
	);
	const [hasBlockingDialog, setHasBlockingDialog] = useState(true);

	useEffect(() => {
		const updateDialogState = () => {
			setHasBlockingDialog(!!document.querySelector('[role="dialog"]:not([data-store-popup-dialog="true"])'));
		};
		const observer = new MutationObserver(updateDialogState);
		observer.observe(document.body, { childList: true, subtree: true });
		updateDialogState();
		return () => observer.disconnect();
	}, []);

	if (!storePopup?.is_active || hasBlockingDialog || (storePopup.master_stores_only && !isMasterStore)) return null;
	return <StorePopup popup={storePopup} country={country} storeEmail={storeEmail} />;
}

function AgentAuthNotice({ authCode, email, intercomOpen }: { authCode: string; email: string; intercomOpen: boolean }) {
	const [expanded, setExpanded] = useState(false);
	const [seen, setSeen] = useState(false);
	const [copied, setCopied] = useState(false);

	// Messenger fills the phone viewport, so hand off to the code dialog when auth is requested mid-chat.
	React.useEffect(() => {
		const mobileQuery = window.matchMedia('(max-width: 767px)');
		const handOffFromMessenger = () => {
			if (intercomOpen && mobileQuery.matches) {
				window.Intercom?.('hide');
				setExpanded(true);
				setSeen(true);
			}
		};
		handOffFromMessenger();
		mobileQuery.addEventListener('change', handOffFromMessenger);
		return () => mobileQuery.removeEventListener('change', handOffFromMessenger);
	}, [intercomOpen]);

	// When the user has seen the code and the dialog closes (for any reason), update Firestore
	React.useEffect(() => {
		if (seen && !expanded) {
			firestore.collection('StoreAgentAuth').doc(email).update({ show_dialog: false }).catch(() => {});
		}
	}, [seen, expanded, email]);

	const handleOpen = () => {
		setExpanded(true);
		setSeen(true);
	};

	const digits = authCode.split('');

	if (expanded) {
		return (
			<Dialog title="" closable cerrarButton={false} zIndex={9000} onClose={() => setExpanded(false)} noContentWrapper className="!max-w-[380px] !p-0 !gap-0 !rounded-2xl !border-0 !shadow-[0_24px_64px_-16px_rgba(0,0,0,0.5)]">
				<div className="flex flex-col">
					{/* Header */}
					<div className="relative flex flex-col items-center overflow-hidden px-6 pb-8 pt-10 sm:px-8">
						<div
							className="pointer-events-none absolute -left-20 -top-28 h-56 w-64 rounded-full opacity-[0.14] blur-[80px]"
							style={{ backgroundColor: 'hsl(184, 73%, 42%)' }}
						/>
						<div
							className="pointer-events-none absolute -bottom-28 -right-20 h-56 w-64 rounded-full opacity-[0.12] blur-[80px]"
							style={{ backgroundColor: 'hsl(190, 60%, 35%)' }}
						/>

						<div className="relative">
							<div
								className="pointer-events-none absolute inset-0 scale-[2] rounded-full opacity-40 blur-xl"
								style={{ backgroundColor: 'hsl(184, 73%, 42%)' }}
							/>
							<div className="relative flex size-14 items-center justify-center rounded-2xl bg-gradient-to-b from-primary-500/20 to-primary-600/5 ring-1 ring-inset ring-primary-400/20 backdrop-blur-sm light:from-primary-100 light:to-primary-50 light:ring-primary-300/30">
								<ShieldCheck className="size-7 text-primary-400 light:text-primary-700" strokeWidth={1.5} />
							</div>
						</div>

						<h2 className="relative mt-6 text-[1.075rem] font-semibold tracking-tight text-neutral-50">
							Código de verificación
						</h2>
						<p className="relative mt-2.5 max-w-[280px] text-[0.8125rem] leading-relaxed text-neutral-400">
							Comparta este código con el agente de soporte para autorizar la acción.
						</p>
					</div>

					{/* Code display */}
					<div className="flex flex-col items-center gap-5 border-t border-white/[0.03] bg-neutral-950/40 px-4 pb-8 pt-6 sm:px-8 light:border-black/[0.04] light:bg-neutral-950/20">
						<div className="flex gap-1.5 sm:gap-2.5">
							{digits.map((digit, i) => (
								<div
									key={i}
									className="flex size-10 items-center justify-center rounded-xl bg-gradient-to-b from-primary-500/15 to-primary-600/5 text-xl font-bold tabular-nums tracking-wide text-primary-300 ring-1 ring-inset ring-primary-400/15 sm:size-12 light:from-primary-50 light:to-primary-100/50 light:text-primary-700 light:ring-primary-300/25"
								>
									{digit}
								</div>
							))}
						</div>
						<button
							onClick={() => {
								navigator.clipboard.writeText(authCode);
								setCopied(true);
								setTimeout(() => setCopied(false), 2000);
							}}
							className="flex items-center gap-1.5 text-[0.75rem] text-neutral-500 transition-colors hover:text-neutral-300"
						>
							{copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
							{copied ? 'Copiado' : 'Copiar código'}
						</button>
						<p className="text-[0.7rem] leading-relaxed text-neutral-600">
							No comparta este código con nadie más.
						</p>
					</div>
				</div>
			</Dialog>
		);
	}

	return (
		<button
			onClick={handleOpen}
			className={classNames(
				'fixed bottom-24 right-4 z-[9000] flex items-center gap-3 rounded-2xl border border-primary-500/20 bg-foreground px-4 py-3 shadow-lg shadow-black/20 transition-all hover:border-primary-400/30 hover:shadow-xl md:bottom-6 light:bg-white light:shadow-neutral-300/30',
				intercomOpen ? 'md:right-[27rem]' : 'md:right-6'
			)}
		>
			<div className="flex size-9 items-center justify-center rounded-xl bg-primary-500/15 ring-1 ring-inset ring-primary-400/15">
				<ShieldCheck className="size-[1.125rem] text-primary-400 light:text-primary-600" strokeWidth={1.75} />
			</div>
			<div className="text-left">
				<p className="text-[0.8125rem] font-semibold text-neutral-100 light:text-neutral-100">Código de verificación</p>
				<p className="text-[0.7rem] text-neutral-500">Toque para ver el código</p>
			</div>
			<div className="ml-1 size-2 animate-pulse rounded-full bg-primary-400" />
		</button>
	);
}

function StoreOffersNotice({ offers, offsetForAgentAuth, userData }: { offers: StoreOffer[]; offsetForAgentAuth: boolean; userData: UserData }) {
	const [expanded, setExpanded] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [now, setNow] = useState(Date.now());
	const [showMap, setShowMap] = useState(false);
	const [rejecting, setRejecting] = useState(false);
	const [typingOtherReason, setTypingOtherReason] = useState(false);
	const [otherReasonText, setOtherReasonText] = useState('');
	const [respondLoading, setRespondLoading] = useState(false);
	const [respondError, setRespondError] = useState<string | null>(null);
	const mapsEnabled = useGoogleMapsEnabled();

	// Live countdown tick
	useEffect(() => {
		if (!expanded) return;
		const interval = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(interval);
	}, [expanded]);

	// Clamp index when offers change
	useEffect(() => {
		if (currentIndex >= offers.length) {
			setCurrentIndex(Math.max(0, offers.length - 1));
		}
	}, [offers.length, currentIndex]);

	// Reset sub-states when switching offers or closing
	useEffect(() => {
		setShowMap(false);
		setRejecting(false);
		setTypingOtherReason(false);
		setOtherReasonText('');
		setRespondError(null);
	}, [currentIndex, expanded]);

	const offer = offers[currentIndex];
	if (!offer) return null;

	const totalDuration = (offer.expires_at.seconds - offer.starts_at.seconds) * 1000;
	const elapsed = now - offer.starts_at.seconds * 1000;
	const fraction = Math.max(0, Math.min(1, 1 - elapsed / totalDuration));
	const remainingMs = Math.max(0, offer.expires_at.seconds * 1000 - now);
	const minutes = Math.floor(remainingMs / 60000);
	const seconds = Math.floor((remainingMs % 60000) / 1000);
	const expired = remainingMs <= 0;

	const initialAmount = offer.locked_price - offer.amount_financed;
	const initialPercent = Math.round((initialAmount / offer.locked_price) * 100);

	const DeviceIcon = (() => {
		const name = offer.device_commercial_name.toLowerCase();
		if (name.includes('laptop') || name.includes('macbook') || name.includes('notebook')) return Laptop;
		if (name.includes('tablet') || name.includes('ipad')) return Tablet;
		return Smartphone;
	})();

	const specs = [offer.storage && `${offer.storage} GB`, offer.ram && `${offer.ram} GB RAM`, offer.color]
		.filter(Boolean)
		.join(' · ');

	const delivery = (() => {
		if (offer.delivery_method === 'pickup') {
			return {
				Icon: Package,
				label: 'Retiro en tienda',
				description: 'El cliente pasará a tu sucursal a recoger el equipo.',
				accent: false,
			};
		}
		if (offer.delivery_type === 'FAST') {
			return {
				Icon: Zap,
				label: 'Envío rápido',
				description: 'Requiere despacho inmediato a través de Yummy al aceptar — ten el equipo listo.',
				accent: true,
			};
		}
		return {
			Icon: Truck,
			label: 'Envío estándar',
			description: 'Debes despacharlo para que llegue al cliente en 3-5 días hábiles.',
			accent: false,
		};
	})();

	const handleRespond = async (
		action: 'accept' | 'reject',
		reason?: 'out_of_stock' | 'price_too_low' | 'other',
		reason_details?: string
	) => {
		setRespondLoading(true);
		setRespondError(null);
		const endpoint =
			`https://api.krece.app/marketplace/offers/${offer.id}/respond` as 'https://api.krece.app/marketplace/offers/<offer_id>/respond';
		const [, error] = await callAPI(
			endpoint,
			'POST',
			{
				action,
				...(reason && { reason }),
				...(reason_details && { reason_details }),
			},
			userData,
			userData.country
		);
		if (error) {
			setRespondError(error || 'Error al responder. Intente de nuevo.');
		}
		// Firestore subscription will remove the offer once backend updates is_active
		setRespondLoading(false);
		setRejecting(false);
		setTypingOtherReason(false);
	};

	if (expanded) {
		return (
			<Dialog
				title=""
				closable={!respondLoading}
				cerrarButton={false}
				zIndex={9000}
				onClose={() => setExpanded(false)}
				noContentWrapper
				className="!w-[90vw] md:!w-[480px] !max-w-[480px] !p-0 !gap-0 !rounded-2xl !border-0 !shadow-[0_25px_60px_-12px_rgba(0,0,0,0.6)]"
			>
				<div className="flex max-h-[85vh] flex-col">
					{/* Header — clean, no blurs */}
					<div className="flex flex-shrink-0 items-center justify-between gap-4 px-6 pb-4 pt-5">
						<div>
							<h2 className="text-[0.95rem] font-semibold tracking-tight text-neutral-50">
								{offers.length === 1 ? 'Orden por confirmar' : 'Ordenes por confirmar'}
							</h2>
							{offers.length > 1 && (
								<p className="mt-0.5 text-[0.75rem] text-neutral-500">
									{currentIndex + 1} de {offers.length}
								</p>
							)}
						</div>
						<div
							className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.7rem] font-medium tabular-nums ${
								expired
									? 'bg-red-500/10 text-red-400 light:bg-red-50 light:text-red-600'
									: fraction < 0.2
										? 'bg-red-500/10 text-red-400 light:bg-red-50 light:text-red-600'
										: fraction < 0.4
											? 'bg-amber-500/10 text-amber-400 light:bg-amber-50 light:text-amber-600'
											: 'bg-primary-500/10 text-primary-400 light:bg-primary-100 light:text-primary-700'
							}`}
						>
							<Clock className="size-3" strokeWidth={2.5} />
							{expired ? 'Expirada' : `${minutes}m ${String(seconds).padStart(2, '0')}s`}
						</div>
					</div>

					{/* Scrollable content */}
					<div className="overflow-y-auto border-t border-white/[0.04] px-6 pb-5 pt-5 light:border-black/[0.06]">
						{/* Device */}
						<div className="flex items-center gap-3.5">
							<div className="flex size-10 flex-shrink-0 items-center justify-center rounded-[10px] bg-neutral-800/70 light:bg-neutral-925 light:ring-1 light:ring-black/[0.06]">
								<DeviceIcon className="size-[1.125rem] text-neutral-400 light:text-neutral-600" strokeWidth={1.75} />
							</div>
							<div className="min-w-0">
								<p className="truncate text-[0.875rem] font-medium text-neutral-100">
									{offer.device_commercial_name}
								</p>
								<p className="mt-0.5 text-[0.75rem] text-neutral-500">
									{offer.brand}{specs ? ` · ${specs}` : ''}
								</p>
							</div>
						</div>

						{/* Pricing — typographic, no boxes */}
						<div className="mt-5 flex items-baseline justify-between gap-8 border-b border-white/[0.04] pb-4 light:border-black/[0.06]">
							<div>
								<p className="text-[0.65rem] font-medium uppercase tracking-widest text-neutral-600 light:text-neutral-500">Precio</p>
								<p className="mt-1 text-[1.35rem] font-semibold tabular-nums leading-none text-neutral-50">
									${offer.locked_price.toFixed(2)}
								</p>
							</div>
							<div className="text-right">
								<p className="text-[0.65rem] font-medium uppercase tracking-widest text-neutral-600 light:text-neutral-500">
									{initialPercent}% Inicial
								</p>
								<p className="mt-1 text-[1.35rem] font-semibold tabular-nums leading-none text-primary-400 light:text-primary-600">
									${initialAmount.toFixed(2)}
								</p>
							</div>
						</div>

						{/* Delivery method */}
						<div
							className={`mt-4 flex items-start gap-3 rounded-xl px-3.5 py-3 ${
								delivery.accent
									? 'bg-primary-500/[0.08] light:bg-primary-100/40'
									: 'bg-neutral-900/50 light:bg-neutral-925'
							}`}
						>
							<div
								className={`flex size-8 flex-shrink-0 items-center justify-center rounded-lg ${
									delivery.accent
										? 'bg-primary-500/20 text-primary-300 light:bg-primary-100 light:text-primary-700'
										: 'bg-neutral-800/80 text-neutral-300 light:bg-white light:text-neutral-500 light:ring-1 light:ring-black/[0.06]'
								}`}
							>
								<delivery.Icon className="size-4" strokeWidth={2} />
							</div>
							<div className="min-w-0 flex-1 pt-[1px]">
								<p
									className={`text-[0.8125rem] font-semibold leading-none ${
										delivery.accent ? 'text-primary-300 light:text-primary-700' : 'text-neutral-100'
									}`}
								>
									{delivery.label}
								</p>
								<p className="mt-1 text-[0.72rem] leading-snug text-neutral-500">
									{delivery.description}
								</p>
							</div>
						</div>

						{/* Address */}
						{offer.address && offer.delivery_method === 'delivery' && (
							<div className="mt-4">
								<button
									onClick={() => setShowMap((v) => !v)}
									className="group flex w-full items-start gap-2 text-left"
								>
									<MapPin className="mt-[3px] size-3.5 flex-shrink-0 text-neutral-600" strokeWidth={2} />
									<p className="min-w-0 flex-1 text-[0.8rem] leading-snug text-neutral-400 transition-colors group-hover:text-neutral-200 light:text-neutral-600 light:group-hover:text-neutral-900">
										{offer.address}
									</p>
									{mapsEnabled && (
										<ChevronDown
											className={`mt-[3px] size-3.5 flex-shrink-0 text-neutral-600 transition-transform duration-200 ${showMap ? 'rotate-180' : ''}`}
											strokeWidth={2}
										/>
									)}
								</button>

								{showMap && mapsEnabled && (
									<div className="mt-3 overflow-hidden rounded-lg">
										<GoogleMap
											mapContainerStyle={{ width: '100%', height: '160px' }}
											center={{ lat: offer.user_lat, lng: offer.user_lng }}
											zoom={16}
											options={{
												disableDefaultUI: true,
												zoomControl: true,
												gestureHandling: 'cooperative',
												styles: [
													{ elementType: 'geometry', stylers: [{ color: '#1a1a2e' }] },
													{ elementType: 'labels.text.stroke', stylers: [{ color: '#1a1a2e' }] },
													{ elementType: 'labels.text.fill', stylers: [{ color: '#8a8a9a' }] },
													{ featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2a2a3e' }] },
													{ featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e1626' }] },
												],
											}}
										>
											<Marker position={{ lat: offer.user_lat, lng: offer.user_lng }} />
										</GoogleMap>
									</div>
								)}
							</div>
						)}

						{/* Error */}
						{respondError && (
							<p className="mt-4 text-center text-[0.8rem] text-red-400">{respondError}</p>
						)}

						{/* Actions */}
						<div className="mt-5">
							{rejecting ? (
								typingOtherReason ? (
									<div className="flex flex-col gap-2.5">
										<p className="text-center text-[0.75rem] text-neutral-500">
											Cuéntanos más <span className="text-neutral-600">(opcional)</span>
										</p>
										<textarea
											value={otherReasonText}
											onChange={(e) => setOtherReasonText(e.target.value)}
											placeholder="Escribe el motivo..."
											rows={3}
											disabled={respondLoading}
											className="w-full resize-none rounded-lg border border-white/[0.06] bg-neutral-900/40 px-3 py-2 text-[0.8rem] leading-relaxed text-neutral-200 placeholder-neutral-600 outline-none transition-colors focus:border-white/[0.12] disabled:opacity-50 light:border-black/[0.08] light:bg-neutral-50 light:text-neutral-800 light:placeholder-neutral-400 light:focus:border-black/[0.16]"
										/>
										<div className="grid grid-cols-[1fr_1.4fr] gap-2">
											<button
												disabled={respondLoading}
												onClick={() => {
													setTypingOtherReason(false);
													setOtherReasonText('');
												}}
												className="flex h-10 items-center justify-center rounded-lg border border-white/[0.06] text-[0.8rem] font-medium text-neutral-400 transition-colors hover:border-white/[0.1] hover:text-neutral-200 disabled:opacity-40 light:border-black/[0.08] light:text-neutral-600 light:hover:border-black/[0.12] light:hover:text-neutral-900"
											>
												Atrás
											</button>
											<button
												disabled={respondLoading}
												onClick={() =>
													handleRespond('reject', 'other', otherReasonText.trim() || undefined)
												}
												className="flex h-10 items-center justify-center rounded-lg bg-neutral-200 text-[0.8rem] font-medium text-neutral-900 transition-colors hover:bg-neutral-100 disabled:opacity-40 light:bg-neutral-50 light:text-neutral-900 light:hover:bg-neutral-100"
											>
												{respondLoading ? <Spinner size={1} /> : 'Enviar'}
											</button>
										</div>
									</div>
								) : (
									<div className="flex flex-col gap-2">
										<p className="text-center text-[0.75rem] text-neutral-500">
											Motivo del rechazo
										</p>
										<div className="grid grid-cols-2 gap-2">
											<button
												disabled={respondLoading}
												onClick={() => handleRespond('reject', 'out_of_stock')}
												className="flex h-10 items-center justify-center rounded-lg border border-white/[0.06] text-[0.8rem] font-medium text-neutral-300 transition-colors hover:border-red-500/30 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-40 light:border-black/[0.08] light:text-neutral-700 light:hover:border-red-300/40 light:hover:bg-red-50 light:hover:text-red-600"
											>
												{respondLoading ? <Spinner size={1} /> : 'Sin stock'}
											</button>
											<button
												disabled={respondLoading}
												onClick={() => handleRespond('reject', 'price_too_low')}
												className="flex h-10 items-center justify-center rounded-lg border border-white/[0.06] text-[0.8rem] font-medium text-neutral-300 transition-colors hover:border-red-500/30 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-40 light:border-black/[0.08] light:text-neutral-700 light:hover:border-red-300/40 light:hover:bg-red-50 light:hover:text-red-600"
											>
												{respondLoading ? <Spinner size={1} /> : 'Precio muy bajo'}
											</button>
										</div>
										<button
											disabled={respondLoading}
											onClick={() => setTypingOtherReason(true)}
											className="flex h-10 items-center justify-center rounded-lg border border-white/[0.06] text-[0.8rem] font-medium text-neutral-300 transition-colors hover:border-white/[0.12] hover:text-neutral-100 disabled:opacity-40 light:border-black/[0.08] light:text-neutral-700 light:hover:border-black/[0.16] light:hover:text-neutral-900"
										>
											Otro motivo
										</button>
										<button
											disabled={respondLoading}
											onClick={() => setRejecting(false)}
											className="mt-0.5 text-[0.75rem] text-neutral-600 transition-colors hover:text-neutral-300"
										>
											Cancelar
										</button>
									</div>
								)
							) : (
								<div className="flex gap-2.5">
									<button
										disabled={respondLoading || expired}
										onClick={() => setRejecting(true)}
										className="flex h-10 flex-1 items-center justify-center rounded-lg border border-white/[0.06] text-[0.8rem] font-medium text-neutral-400 transition-colors hover:border-white/[0.1] hover:text-neutral-200 disabled:opacity-30 light:border-black/[0.08] light:text-neutral-600 light:hover:border-black/[0.12] light:hover:text-neutral-900"
									>
										Rechazar
									</button>
									<button
										disabled={respondLoading || expired}
										onClick={() => handleRespond('accept')}
										className="flex h-10 flex-[1.4] items-center justify-center rounded-lg bg-primary-500 text-[0.8rem] font-medium text-white transition-all hover:bg-primary-400 disabled:opacity-30 light:bg-primary-600 light:hover:bg-primary-500"
									>
										{respondLoading ? <Spinner size={1} /> : 'Aceptar oferta'}
									</button>
								</div>
							)}
						</div>

						{/* Pagination */}
						{offers.length > 1 && (
							<div className="mt-5 flex items-center justify-center gap-4">
								<button
									onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
									disabled={currentIndex === 0}
									className="text-neutral-600 transition-colors hover:text-neutral-200 disabled:opacity-20"
								>
									<ChevronLeft className="size-4" />
								</button>

								<div className="flex items-center gap-2">
									{offers.map((_, i) => (
										<button
											key={i}
											onClick={() => setCurrentIndex(i)}
											className={`rounded-full transition-all duration-200 ${
												i === currentIndex
													? 'h-1.5 w-5 bg-primary-400 light:bg-primary-600'
													: 'size-1.5 bg-neutral-700 hover:bg-neutral-500 light:bg-neutral-300'
											}`}
										/>
									))}
								</div>

								<button
									onClick={() => setCurrentIndex((i) => Math.min(offers.length - 1, i + 1))}
									disabled={currentIndex === offers.length - 1}
									className="text-neutral-600 transition-colors hover:text-neutral-200 disabled:opacity-20"
								>
									<ChevronRight className="size-4" />
								</button>
							</div>
						)}
					</div>
				</div>
			</Dialog>
		);
	}

	return (
		<button
			onClick={() => setExpanded(true)}
			className={`fixed ${
				offsetForAgentAuth ? 'bottom-[10.5rem] md:bottom-[5.5rem]' : 'bottom-24 md:bottom-6'
			} right-4 z-[9000] flex items-center gap-3 rounded-2xl border border-primary-500/20 bg-foreground px-4 py-3 shadow-lg shadow-black/20 transition-all hover:border-primary-400/30 hover:shadow-xl md:right-6 light:bg-white light:shadow-neutral-300/30`}
		>
			<div className="flex size-9 items-center justify-center rounded-xl bg-primary-500/15 ring-1 ring-inset ring-primary-400/15">
				<Tag className="size-[1.125rem] text-primary-400 light:text-primary-600" strokeWidth={1.75} />
			</div>
			<div className="text-left">
				<p className="text-[0.8125rem] font-semibold text-neutral-100 light:text-neutral-100">
					{offers.length === 1 ? '1 orden por confirmar' : `${offers.length} ordenes por confirmar`}
				</p>
				<p className="text-[0.7rem] text-neutral-500">
					Toque para ver {offers.length === 1 ? 'la orden' : 'las ordenes'}
				</p>
			</div>
			<div className="ml-1 size-2 animate-pulse rounded-full bg-primary-400" />
		</button>
	);
}

function NewVersionBanner() {
	return (
		<div className="fixed inset-x-0 top-0 z-[9999] select-none bg-primary-500 shadow-lg backdrop-blur-sm">
			<div className="mx-auto max-w-4xl px-4 py-6">
				<div className="flex flex-col items-center justify-between gap-4 md:flex-row">
					<div className="hidden size-12 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 md:flex">
						<RefreshCw className="h-6 w-6 text-primary-600" />
					</div>

					<div className="flex-1">
						<h3 className="flex items-center justify-center gap-2 font-semibold text-white light:text-black md:block md:text-lg">
							<RefreshCw className="size-4 md:hidden" />
							¡Nueva actualización disponible!
						</h3>
						<p className="mt-1 text-center text-sm text-primary-100 light:text-neutral-200 md:text-left">
							Debe refrescar la página para actualizar y evitar errores.
						</p>
					</div>

					<div className="flex flex-shrink-0 items-center gap-4">
						<button
							onClick={() => {
								window.location.reload();
							}}
							className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-primary-600 shadow-sm transition-all hover:bg-blue-50 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
						>
							<AlertCircle className="h-4 w-4" />
							Actualizar ahora
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
